import dayjs, { Dayjs } from 'dayjs';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, FormikErrors, useField } from 'formik';
import { FormattedMessage } from 'react-intl';
import { ReactElement, ReactNode, useEffect, useState } from 'react';

import { PoolDatePicker, PoolDatePickerProps } from './pool-date-picker';

export interface FormikPoolDatePickerProps extends Omit<PoolDatePickerProps, 'name' | 'onChange'> {
    name: string; // redefined as required
    onChange?: (
        value: Dayjs | null,
        helper: FieldHelperProps<Dayjs | null>,
        field: FieldInputProps<Dayjs | null>,
        meta: FieldMetaProps<Dayjs | null>
    ) => void;
}

export const FormikPoolDatePicker = (props: FormikPoolDatePickerProps): ReactElement => {
    const { onChange, ...pickerProps } = props;
    const [field, meta, helper] = useField<Dayjs | null>(props.name);
    const hasFormikError = meta.touched && !!meta.error;
    const [currentValue, setCurrentValue] = useState<Dayjs | null>(field.value ? dayjs(field.value).hour(12) : null);

    useEffect(() => {
        const value = field.value ? dayjs(field.value).hour(12) : null;
        setCurrentValue(value);
        onChange?.(value, helper, field, meta);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    const handleChange = (value: Dayjs | null): void => {
        setCurrentValue(value);
    };

    const setFieldValue = (value: Dayjs | null): Promise<void | FormikErrors<Dayjs | null>> =>
        helper.setValue(value?.hour(12) ?? null);

    const getHelperText = (): ReactNode => {
        if (props.disabled || props.readOnly) {
            return undefined;
        }
        if (hasFormikError) {
            return <FormattedMessage id={meta.error} />;
        }
        return undefined;
    };

    return (
        <PoolDatePicker
            {...pickerProps}
            slotProps={{
                textField: {
                    onBlur: () => setFieldValue(currentValue),
                },
            }}
            helperText={getHelperText()}
            name={field.name}
            error={hasFormikError}
            value={currentValue}
            onChange={handleChange}
            onAccept={value => setFieldValue(value)}
        />
    );
};
